/**
 * The AI surfaces — data access (design C12 §6.4, §6.6, §10).
 *
 * Four properties of these endpoints decide the screens' shape:
 *
 *  1. **Every write still goes through preview → confirm.** There is one
 *     `Confirm` in the product and it is `assistant.go`'s. The Assistant panel
 *     renders the preview card and calls `aiAssistantToolPreviewsConfirm`; it
 *     never posts a business document itself, at any autonomy level.
 *  2. **Figures are computed in Go and the model only narrates.** A briefing
 *     section carries `figures` (never empty — `abs_figures_check`) and a
 *     `narrative` that may be empty. The screens render the figures first and
 *     the prose second, visually, because CLAUDE.md §12's rule is only true if
 *     the reader can see which is which. **Nothing here computes a figure.**
 *  3. **Accuracy and coverage are server strings.** `accuracy`,
 *     `meanAbsolutePercentageError`, `measuredRatio`, `notTakenRatio` and
 *     `unmeasurableRatio` all arrive formatted as decimals. §7.8's whole point
 *     is that rejections are not errors, and a client that re-derived the ratio
 *     would be free to get that wrong in a second place.
 *  4. **Why a level is unavailable is a server field, not a client inference.**
 *     `delegationBlockedReason` names the shut door in the same vocabulary as
 *     the two 422 codes, and `evidenceSamples`/`evidenceAccuracy` against
 *     `requiredSamples`/`requiredAccuracy` are what the screen shows instead of
 *     a disabled option with no explanation.
 */

import {
  aiAccuracyByType,
  aiAgentsList,
  aiAgentsPatch,
  aiAgentsRun,
  aiAgentRunsList,
  aiAssistantConversationsCreate,
  aiAssistantConversationsList,
  aiAssistantConversationsMessages,
  aiAssistantConversationsSend,
  aiAssistantToolPreviewsConfirm,
  aiAssistantToolPreviewsDiscard,
  aiBriefingsGet,
  aiBriefingsList,
  aiBriefingsRead,
  aiCapabilitiesGet,
  aiCapabilitiesList,
  aiCapabilitiesPatch,
  aiCapabilitiesSetAutonomy,
  aiConfigAnomaliesAcknowledge,
  aiConfigAnomaliesCoverage,
  aiConfigAnomaliesDismiss,
  aiConfigAnomaliesList,
  aiConfigAnomaliesSweep,
  aiConfigAnomalyRulesList,
  aiRecommendationsAccept,
  aiRecommendationsGet,
  aiRecommendationsList,
  aiRecommendationsReject,
  aiSettingsGet,
  type AIAccuracyByTypeDTO,
  type AIAccuracyRowDTO,
  type AIAgentDTO,
  type AIAgentRunDTO,
  type AIAnomalySweepResult,
  type AIBriefingDTO,
  type AIBriefingDetailDTO,
  type AIBriefingSectionDTO,
  type AICapabilityDTO,
  type AICapabilityDetailDTO,
  type AIConfigAnomalyCoverageDTO,
  type AIConfigAnomalyDTO,
  type AIConfigAnomalyRuleDTO,
  type AIRecommendationDTO,
  type AIRecommendationDetailDTO,
  type AISettingsDTO,
  type ConversationDTO,
  type ListAIBriefingsQuery,
  type ListAIConfigAnomaliesQuery,
  type ListAIRecommendationsQuery,
  type MessageDTO,
  type PageAIBriefingDTO,
  type PageAIConfigAnomalyDTO,
  type PageAIRecommendationDTO,
  type PreviewDTO,
} from '@erpx/api'

import { api } from '@/api/client'

export type {
  AIAccuracyByTypeDTO,
  AIAccuracyRowDTO,
  AIAgentDTO,
  AIAgentRunDTO,
  AIAnomalySweepResult,
  AIBriefingDTO,
  AIBriefingDetailDTO,
  AIBriefingSectionDTO,
  AICapabilityDTO,
  AICapabilityDetailDTO,
  AIConfigAnomalyCoverageDTO,
  AIConfigAnomalyDTO,
  AIConfigAnomalyRuleDTO,
  AIRecommendationDTO,
  AIRecommendationDetailDTO,
  AISettingsDTO,
  ConversationDTO,
  MessageDTO,
  PreviewDTO,
}

export const AI_KEYS = {
  capabilities: ['ai', 'capabilities'] as const,
  capability: (code: string) => ['ai', 'capabilities', code] as const,
  settings: (companyId: number) => ['ai', 'settings', companyId] as const,
  recommendations: ['ai', 'recommendations'] as const,
  recommendationsPage: (params: Record<string, unknown>) =>
    ['ai', 'recommendations', 'list', params] as const,
  recommendation: (id: number) => ['ai', 'recommendations', id] as const,
  accuracy: (params: Record<string, unknown>) => ['ai', 'accuracy', params] as const,
  agents: ['ai', 'agents'] as const,
  agentRuns: (params: Record<string, unknown>) => ['ai', 'agent-runs', params] as const,
  briefings: ['ai', 'briefings'] as const,
  briefingsPage: (params: Record<string, unknown>) => ['ai', 'briefings', 'list', params] as const,
  briefing: (id: number) => ['ai', 'briefings', id] as const,
  anomalies: ['ai', 'config-anomalies'] as const,
  anomaliesPage: (params: Record<string, unknown>) =>
    ['ai', 'config-anomalies', 'list', params] as const,
  anomalyCoverage: ['ai', 'config-anomalies', 'coverage'] as const,
  anomalyRules: ['ai', 'config-anomaly-rules'] as const,
  conversations: ['ai', 'conversations'] as const,
  messages: (id: number) => ['ai', 'conversations', id, 'messages'] as const,
} as const

/** The three levels, weakest first. Used to order the selector, never to compare money. */
export const AUTONOMY_LEVELS = ['assistive', 'supervised', 'delegated'] as const

export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number]

export const DISPOSITIONS = [
  'pending',
  'accepted',
  'rejected',
  'expired',
  'auto_confirmed',
  'routed',
] as const

/* --------------------------------------------------------------- capabilities */

export function listCapabilities(): Promise<AICapabilityDTO[]> {
  return aiCapabilitiesList(api(), { limit: 200 }).then((page) => page.items)
}

export function getCapability(code: string): Promise<AICapabilityDetailDTO> {
  return aiCapabilitiesGet(api(), { code })
}

export function patchCapability(
  code: string,
  body: {
    confidenceThreshold?: string | null
    tolerancePct?: string | null
    toleranceAbs?: string | null
    isEnabled?: boolean | null
  },
): Promise<AICapabilityDTO> {
  return aiCapabilitiesPatch(api(), { code }, body)
}

export function setAutonomy(
  code: string,
  autonomy: AutonomyLevel,
  note: string,
  idempotencyKey: string,
): Promise<AICapabilityDTO> {
  return aiCapabilitiesSetAutonomy(
    api(),
    { code },
    { autonomy, ...(note === '' ? {} : { note }) },
    idempotencyKey,
  )
}

export function getAiSettings(companyId: number): Promise<AISettingsDTO> {
  return aiSettingsGet(api(), { companyId })
}

/* ------------------------------------------------------------ recommendations */

export function listRecommendations(
  filters: { capability?: string; type?: string; disposition?: string; companyId?: number | null },
  page: { cursor?: string; limit?: number } = {},
): Promise<PageAIRecommendationDTO> {
  const query: ListAIRecommendationsQuery = { limit: page.limit ?? 50 }
  if (page.cursor !== undefined && page.cursor !== '') query.cursor = page.cursor
  if (filters.capability !== undefined && filters.capability !== '') {
    query.capability = filters.capability
  }
  if (filters.type !== undefined && filters.type !== '') query.type = filters.type
  if (isDisposition(filters.disposition)) query.disposition = filters.disposition
  if (filters.companyId !== undefined && filters.companyId !== null) {
    query.companyId = filters.companyId
  }
  return aiRecommendationsList(api(), query)
}

function isDisposition(value: string | undefined): value is (typeof DISPOSITIONS)[number] {
  return value !== undefined && (DISPOSITIONS as readonly string[]).includes(value)
}

export function getRecommendation(id: number): Promise<AIRecommendationDetailDTO> {
  return aiRecommendationsGet(api(), { id: String(id) })
}

/**
 * Accept a pending recommendation.
 *
 * The server resolves the attached preview through the same `ConfirmPreview`
 * every Assistant confirmation uses — there is no second write path, and this
 * function's existence must not suggest one.
 */
export function acceptRecommendation(
  id: number,
  idempotencyKey: string,
): Promise<AIRecommendationDetailDTO> {
  return aiRecommendationsAccept(api(), { id: String(id) }, idempotencyKey)
}

export function rejectRecommendation(
  id: number,
  reason: string,
  idempotencyKey: string,
): Promise<AIRecommendationDetailDTO> {
  return aiRecommendationsReject(api(), { id: String(id) }, { reason }, idempotencyKey)
}

export function accuracyByType(
  capability: string,
  companyId: number | null,
): Promise<AIAccuracyByTypeDTO> {
  return aiAccuracyByType(api(), {
    ...(capability === '' ? {} : { capability }),
    ...(companyId === null ? {} : { companyId }),
  })
}

/* -------------------------------------------------------- agents and briefings */

export function listAgents(): Promise<AIAgentDTO[]> {
  return aiAgentsList(api(), { limit: 100 }).then((page) => page.items)
}

export function patchAgent(
  code: string,
  body: { isEnabled?: boolean | null; recipientPositionIds?: number[] | null; rowVersion?: number },
): Promise<AIAgentDTO> {
  return aiAgentsPatch(api(), { code }, body)
}

export function runAgent(
  code: string,
  companyId: number,
  businessDate: string,
  idempotencyKey: string,
): Promise<AIBriefingDetailDTO> {
  return aiAgentsRun(
    api(),
    { code },
    { companyId, ...(businessDate === '' ? {} : { businessDate }) },
    idempotencyKey,
  )
}

export function listAgentRuns(
  agentCode: string,
  status: string,
): Promise<AIAgentRunDTO[]> {
  return aiAgentRunsList(api(), {
    limit: 50,
    ...(agentCode === '' ? {} : { agentCode }),
    ...(status === '' ? {} : { status }),
  }).then((page) => page.items)
}

export function listBriefings(
  filters: { agentCode?: string; companyId?: number | null; unreadOnly?: boolean },
  page: { cursor?: string; limit?: number } = {},
): Promise<PageAIBriefingDTO> {
  const query: ListAIBriefingsQuery = { limit: page.limit ?? 50 }
  if (page.cursor !== undefined && page.cursor !== '') query.cursor = page.cursor
  if (filters.agentCode !== undefined && filters.agentCode !== '') {
    query.agentCode = filters.agentCode
  }
  if (filters.companyId !== undefined && filters.companyId !== null) {
    query.companyId = filters.companyId
  }
  if (filters.unreadOnly === true) query.unreadOnly = true
  return aiBriefingsList(api(), query)
}

export function getBriefing(id: number): Promise<AIBriefingDetailDTO> {
  return aiBriefingsGet(api(), { id: String(id) })
}

export function markBriefingRead(id: number, idempotencyKey: string): Promise<AIBriefingDTO> {
  return aiBriefingsRead(api(), { id: String(id) }, idempotencyKey)
}

/* ------------------------------------------------------------ config anomalies */

export function listAnomalies(
  filters: { ruleCode?: string; severity?: string; status?: string },
  page: { cursor?: string; limit?: number } = {},
): Promise<PageAIConfigAnomalyDTO> {
  const query: ListAIConfigAnomaliesQuery = { limit: page.limit ?? 50 }
  if (page.cursor !== undefined && page.cursor !== '') query.cursor = page.cursor
  if (filters.ruleCode !== undefined && filters.ruleCode !== '') query.ruleCode = filters.ruleCode
  if (filters.severity !== undefined && filters.severity !== '') query.severity = filters.severity
  if (filters.status !== undefined && filters.status !== '') query.status = filters.status
  return aiConfigAnomaliesList(api(), query)
}

export function anomalyCoverage(): Promise<AIConfigAnomalyCoverageDTO> {
  return aiConfigAnomaliesCoverage(api())
}

export function acknowledgeAnomaly(
  id: number,
  reasonCode: string,
  note: string,
  idempotencyKey: string,
): Promise<AIConfigAnomalyDTO> {
  return aiConfigAnomaliesAcknowledge(
    api(),
    { id: String(id) },
    { reasonCode, ...(note === '' ? {} : { note }) },
    idempotencyKey,
  )
}

export function dismissAnomaly(
  id: number,
  reasonCode: string,
  note: string,
  idempotencyKey: string,
): Promise<AIConfigAnomalyDTO> {
  return aiConfigAnomaliesDismiss(
    api(),
    { id: String(id) },
    { reasonCode, ...(note === '' ? {} : { note }) },
    idempotencyKey,
  )
}

/**
 * The rule catalogue, for its labels.
 *
 * An anomaly row carries a `ruleCode` and not the rule's `labelKey`, so the
 * screen resolves the name through the catalogue rather than assembling an i18n
 * path out of a code — a code like `ai.config.churn` does not compose into
 * `ai.anomalyRule.churn.label` by any rule a reader could rely on.
 */
export function listAnomalyRules(): Promise<AIConfigAnomalyRuleDTO[]> {
  return aiConfigAnomalyRulesList(api())
}

export function sweepAnomalies(idempotencyKey: string): Promise<AIAnomalySweepResult> {
  return aiConfigAnomaliesSweep(api(), idempotencyKey)
}

/* ------------------------------------------------------------------ assistant */

export function listConversations(): Promise<ConversationDTO[]> {
  return aiAssistantConversationsList(api(), { limit: 20 }).then((page) => page.items)
}

export function startConversation(idempotencyKey: string): Promise<ConversationDTO> {
  return aiAssistantConversationsCreate(api(), {}, idempotencyKey)
}

export function listMessages(conversationId: number): Promise<MessageDTO[]> {
  return aiAssistantConversationsMessages(api(), { id: String(conversationId) }, { limit: 100 }).then(
    (page) => page.items,
  )
}

export function sendMessage(
  conversationId: number,
  message: string,
  idempotencyKey: string,
): Promise<MessageDTO> {
  return aiAssistantConversationsSend(
    api(),
    { id: String(conversationId) },
    { message },
    idempotencyKey,
  )
}

export function confirmPreview(id: number, idempotencyKey: string): Promise<PreviewDTO> {
  return aiAssistantToolPreviewsConfirm(api(), { id: String(id) }, idempotencyKey)
}

export function discardPreview(id: number, idempotencyKey: string): Promise<PreviewDTO> {
  return aiAssistantToolPreviewsDiscard(api(), { id: String(id) }, idempotencyKey)
}

/* ------------------------------------------------------------- presentation */

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent'

export function severityTone(severity: string): Tone {
  switch (severity) {
    case 'urgent':
      return 'danger'
    case 'attention':
      return 'warning'
    default:
      return 'info'
  }
}

export function dispositionTone(disposition: string): Tone {
  switch (disposition) {
    case 'accepted':
    case 'auto_confirmed':
      return 'success'
    case 'rejected':
      return 'neutral'
    case 'routed':
      return 'warning'
    case 'expired':
      return 'danger'
    default:
      return 'accent'
  }
}

export function runTone(status: string): Tone {
  switch (status) {
    case 'complete':
      return 'success'
    case 'degraded':
      return 'warning'
    case 'failed':
      return 'danger'
    default:
      return 'neutral'
  }
}

/**
 * A section the model could not narrate, but whose figures are complete.
 *
 * §4.7.4's `figures_only` fallback is the one C12 leans on hardest: a provider
 * outage must degrade a briefing to its numbers, never to an error, because the
 * numbers were computed in Go and were never the model's to produce.
 */
export function isFiguresOnly(section: Pick<AIBriefingSectionDTO, 'narrative'>): boolean {
  const prose = section.narrative
  return prose === undefined || prose.trim() === ''
}

/** A figure, as text. Never parsed, never arithmetic — the server computed it. */
export function figureText(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

/**
 * Where a citation points, or null when this app has no screen for that type.
 *
 * `Citation` carries a doc type and an id and **not** a route (unlike a
 * briefing's citation, which does). Rather than invent a URL for every type, the
 * map names the types the shipped tools actually emit that have a by-id screen and
 * returns null for anything else (`cash.bank_account` and `bankrec.exception` have
 * no by-id screen: reconciliation exceptions are a list) — an unlinked citation is a fact; a link to a route that does
 * not exist is a 404 the reader will blame on the Assistant.
 *
 * `rvm.displacement` has no screen in this packet (it is the RVM packet's), so
 * it is deliberately absent and its citations render as text.
 */
const CITATION_ROUTES: Record<string, (id: number) => string> = {
  'ar.customer': (id) => `/ar/customers/${id}`,
  'procurement.po': (id) => `/procurement/orders/${id}`,
  // The Treasury Copilot's read tools (FT18-B27A).
  'treasury.forecast_run': (id) => `/treasury/forecasts/${id}`,
  'treasury.position': (id) => `/treasury/positions/${id}`,
  'treasury.wallet': (id) => `/treasury/wallets/${id}`,
  'treasury.facility': (id) => `/treasury/facilities/${id}`,
  'cash.transfer_instruction': (id) => `/cash/transfer-instructions/${id}`,
}

export function citationRoute(citation: {
  docType: string
  docId?: number
}): string | null {
  const build = CITATION_ROUTES[citation.docType]
  if (build === undefined) return null
  const id = citation.docId
  if (id === undefined || id === 0) return null
  return build(id)
}

/** `figures` on a recommendation is a JSON document the server assembled. */
export function parseFigures(figures: string): [string, unknown][] {
  if (figures === '') return []
  try {
    const parsed: unknown = JSON.parse(figures)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return []
    return Object.entries(parsed as Record<string, unknown>)
  } catch {
    return []
  }
}
